import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Instagram, Facebook, Phone, MapPin } from "lucide-react";
import { content } from "../lib/i18n";
import { useLang } from "../context/LanguageContext";

const Footer = () => {
  const { lang } = useLang();
  const fr = content.footer.fr;
  const en = content.footer.en;
  const isBoth = lang === "both";
  const data = lang === "en" ? en : fr;
  const year = new Date().getFullYear();

  return (
    <footer data-testid="site-footer" className="bb-footer">
      <div className="mx-auto max-w-[1320px] px-5 md:px-8 lg:px-12 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <div className="max-w-2xl">
            <img
              src={`${process.env.PUBLIC_URL || ""}/brand/logo-white.png`}
              alt="Big Boss Snack Bar"
              className="h-20 md:h-28 w-auto object-contain"
              style={{ maxWidth: "300px" }}
            />
            <p
              data-testid="footer-slogan"
              className="mt-6 font-display italic text-xl sm:text-2xl md:text-3xl text-white/85 normal-case font-bold leading-tight"
            >
              {isBoth ? fr.slogan : data.slogan}
            </p>
            {isBoth && (
              <p className="mt-1 font-display italic text-base sm:text-lg md:text-xl text-[var(--bb-yellow)]/80 normal-case font-bold leading-tight">
                {en.slogan}
              </p>
            )}
          </div>

          <a
            href={content.externalLinks.order}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-main-order-cta"
            className="bb-btn bb-btn-yellow bb-btn-lg shrink-0"
          >
            <span>{lang === "en" ? "🔥 Order now" : "🔥 Commander maintenant"}</span>
            <ArrowUpRight size={16} strokeWidth={2.5} />
          </a>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--bb-yellow)] font-bold mb-3 flex items-center gap-2">
              <MapPin size={12} strokeWidth={2.5} />
              <span>{lang === "en" ? "Find us" : "Nous trouver"}</span>
            </div>
            <p className="text-base text-white/85 leading-relaxed max-w-[280px]">
              {content.contactInfo.address}
            </p>
            <a
              href={content.contactInfo.phoneHref}
              className="mt-4 inline-flex items-center gap-2 text-base text-white/85 hover:text-[var(--bb-yellow)] transition-colors"
            >
              <Phone size={14} strokeWidth={2.5} />
              <span>{content.contactInfo.phone}</span>
            </a>
          </div>

          <div className="md:col-span-4">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--bb-yellow)] font-bold mb-3">
              {lang === "en" ? "Hours" : "Horaires"}
            </div>
            <p className="text-base text-white/85 leading-relaxed">
              {lang === "en" ? "Mon – Sun" : "Lun – Dim"}
              <br />
              11:00 – 14:00 · 17:00 – 22:30
            </p>
          </div>

          <div className="md:col-span-4">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--bb-yellow)] font-bold mb-3">
              {data.quickLinks}
            </div>
            <ul className="space-y-2.5">
              <li>
                <a
                  href={content.externalLinks.order}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-order-link"
                  className="inline-flex items-center gap-1.5 text-base text-white/85 hover:text-[var(--bb-yellow)] transition-colors"
                >
                  <span>{lang === "en" ? "Order online" : "Commander"}</span>
                  <ArrowUpRight size={14} strokeWidth={2.5} />
                </a>
              </li>
              <li>
                <a
                  href={content.externalLinks.reservation}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-reserve-link"
                  className="inline-flex items-center gap-1.5 text-base text-white/85 hover:text-[var(--bb-yellow)] transition-colors"
                >
                  <span>{lang === "en" ? "Reserve a table" : "Réserver"}</span>
                  <ArrowUpRight size={14} strokeWidth={2.5} />
                </a>
              </li>
              <li>
                <a href="#menu" className="text-base text-white/85 hover:text-[var(--bb-yellow)] transition-colors">
                  Menu
                </a>
              </li>
              <li>
                <a href="#contact" className="text-base text-white/85 hover:text-[var(--bb-yellow)] transition-colors">
                  Contact
                </a>
              </li>
            </ul>

            <div className="mt-6">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--bb-yellow)] font-bold mb-3">
                {data.followUs}
              </div>
              <div className="flex items-center gap-3">
                <a
                  href="#"
                  aria-label="Instagram"
                  data-testid="footer-social-instagram"
                  className="h-10 w-10 rounded-full border-2 border-white/30 hover:bg-[var(--bb-yellow)] hover:border-[var(--bb-yellow)] hover:text-black flex items-center justify-center transition-colors text-white"
                >
                  <Instagram size={16} strokeWidth={2.5} />
                </a>
                <a
                  href="#"
                  aria-label="Facebook"
                  data-testid="footer-social-facebook"
                  className="h-10 w-10 rounded-full border-2 border-white/30 hover:bg-[var(--bb-yellow)] hover:border-[var(--bb-yellow)] hover:text-black flex items-center justify-center transition-colors text-white"
                >
                  <Facebook size={16} strokeWidth={2.5} />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/15 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[11px] tracking-[0.18em] uppercase text-white/45 font-bold">
          <span>© {year} Big Boss Snack Bar — {data.rights}</span>
          <span>La Neuveville · Suisse</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
